import createClient, { type Middleware } from "openapi-fetch";
import { APP_HEADER } from "../appVersion";
import { getConfig } from "../config";
import type { Realm } from "../auth/realms";
import { accessToken, renewAccessToken } from "../auth/token";
import { currentOrgId } from "../features/org/orgStore";
import { globalApiBase, orgApiBase } from "../servers/discovery";
import type { paths } from "./generated/vision-api";
import { NetworkError, toApiError } from "./problem";

export type VisionClient = ReturnType<typeof createClient<paths>>;

/**
 * The API origin for a request (servers-and-developers WEB-SRV-BOOT-02, -03): the staff console and `GET /v1/me` go
 * to the global API, a customer's other routes to the `api` of the organisation's region. Without a discovery
 * document (staging, a local build) everything goes to `config.json` `apiBase`, as before. "" is the app's origin.
 */
export function apiBase(realm: Realm, orgId?: string, path = ""): string {
  const global = realm === "ops" || path === "/v1/me";
  return (global ? globalApiBase() : orgApiBase(orgId ?? currentOrgId())) ?? getConfig().apiBase;
}

/** The request again at another API origin; the body is read once, so no streaming body has to be re-sent. */
async function retarget(request: Request, base: string): Promise<Request> {
  const from = new URL(request.url);
  const to = new URL(from.pathname + from.search, base || window.location.origin);
  if (to.href === from.href) return request;
  const body = request.body ? await request.clone().arrayBuffer() : undefined;
  return new Request(to, {
    method: request.method,
    headers: request.headers,
    body,
    signal: request.signal,
    credentials: request.credentials,
    cache: request.cache,
    redirect: request.redirect,
  });
}

function authMiddleware(realm: Realm): Middleware {
  return {
    async onRequest({ request }) {
      // An expired token is renewed first, not left out: after sleep the screens refetch before the renewal on the
      // library's timer has run.
      const token = await accessToken(realm);
      if (token) request.headers.set("Authorization", `Bearer ${token}`);
      // The app and its version on every request, for the server-derived usage statistics (usage-metrics UM-02). The
      // server records nothing for a person who switched them off (UM-05), nor for staff (UM-04).
      request.headers.set("X-Creavision-App", APP_HEADER);
      // The organisation the customer app acts in (WEB-F-SH-02, BE-F-ID-03). Ops routes take no X-Org. A call that
      // names its own X-Org keeps it: a shared device's page acts in the organisation that shared it (WEB-SWM-02).
      const org = realm === "customers" ? currentOrgId() : undefined;
      if (org && !request.headers.has("X-Org")) request.headers.set("X-Org", org);
      // A call that names its own organisation goes to that organisation's region.
      const path = new URL(request.url).pathname;
      const base = apiBase(realm, request.headers.get("X-Org") ?? undefined, path);
      return retarget(request, base);
    },
  };
}

const clients = new Map<Realm, VisionClient>();

/** The generated vision-api client for one realm, with its token, X-Org and X-Creavision-App added to every request. */
export function apiClient(realm: Realm): VisionClient {
  let c = clients.get(realm);
  if (!c) {
    c = createClient<paths>({
      baseUrl: getConfig().apiBase,
      fetch: async (req: Request) => {
        try {
          // A 401 is tried once more with renewed tokens: the server may have refused a token the browser still
          // counts as valid (its clock, or a token revoked by a renewal elsewhere).
          const again = req.clone();
          const res = await fetch(req);
          if (res.status !== 401) return res;
          const token = await renewAccessToken(realm);
          if (!token) return res;
          again.headers.set("Authorization", `Bearer ${token}`);
          return await fetch(again);
        } catch (e) {
          throw new NetworkError(e);
        }
      },
    });
    c.use(authMiddleware(realm));
    clients.set(realm, c);
  }
  return c;
}

/**
 * The X-Org header for a call made for one organisation: the device page's calls name theirs, so the page of a device
 * shared from another organisation acts there while the rest of the app stays in the selected one (WEB-SWM-02).
 */
export function orgHeader(orgId: string): { "X-Org"?: string } {
  return orgId ? { "X-Org": orgId } : {};
}

export function resetClientsForTests(): void {
  clients.clear();
}

type Result<T> = { data?: T; error?: unknown; response: Response };

/** Returns the data of a call, or throws ApiError with the problem document. Use inside query functions. */
export async function unwrap<T>(call: Promise<Result<T>>): Promise<T> {
  const { data, error, response } = await call;
  if (!response.ok) throw toApiError(response.status, error, response.headers.get("Retry-After"));
  return data as T;
}
